import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useId, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { formatMoney } from '@/format/money'
import { StateView } from '@/card/record'
import {
  AdminPage,
  AdminState,
  EditLayer,
  Field,
  LayerButtons,
  Notice,
  OnOffBadge,
  PrimaryButton,
  RowMenu,
  SkeletonTable,
  Switch,
  Tokens,
  initials,
  isForbidden,
  useAdminEditing,
} from './AdminShell'
import type { Approvers, Threshold, ThresholdInput } from './api'
import {
  createThreshold,
  fetchApprovers,
  fetchCurrencies,
  fetchOrganizations,
  fetchThresholds,
  updateThreshold,
} from './api'

/**
 * Пороги согласования (тикет #97, макет AdmThresholds.dc.html).
 *
 * Порог проверяется в момент отправки документа, а не флагом на нём (#28), поэтому правка
 * действует со следующей отправки — это сказано в подсказке экрана, чтобы никто не ждал,
 * что уже отправленные документы «пересогласуются».
 *
 * Тип документа предлагается только из тех, что сервер действительно проверяет: порог на
 * тип, который никто не читает, выглядел бы подключённым и ничего бы не делал. Условие у
 * порога одно по устройству — скидка от или сумма от (сработает любое из заданных); иных
 * полей условия (отсрочка, просрочка клиента) в пороге нет, и экран их не предлагает.
 */
export function ThresholdsPage() {
  const { t, locale } = useApp()
  const editing = useAdminEditing()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Draft | null>(null)
  const [type, setType] = useState('all')
  const [activeOnly, setActiveOnly] = useState(false)

  const thresholds = useQuery({ queryKey: ['admin-thresholds'], queryFn: ({ signal }) => fetchThresholds(signal) })
  const approvers = useQuery({ queryKey: ['admin-approvers'], queryFn: ({ signal }) => fetchApprovers(signal) })
  const organizations = useQuery({ queryKey: ['admin-organizations'], queryFn: ({ signal }) => fetchOrganizations(signal) })

  const toggle = useMutation({
    mutationFn: (threshold: Threshold) => updateThreshold(threshold.id, toInput({ ...fromThreshold(threshold), active: !threshold.active })),
    onSuccess: () => {
      editing.clear()
      void queryClient.invalidateQueries({ queryKey: ['admin-thresholds'] })
    },
    onError: editing.fail,
  })

  const all = thresholds.data?.thresholds ?? []
  const types = thresholds.data?.documentTypes ?? []
  const typesShown = [...new Set([...types, ...all.map((rule) => rule.entityType)])]
  const visible = all
    .filter((rule) => type === 'all' || rule.entityType === type)
    .filter((rule) => !activeOnly || rule.active)
  const chainType = type !== 'all' ? type : (typesShown.find((candidate) => all.some((rule) => rule.entityType === candidate && rule.active)) ?? null)
  const chain = all.filter((rule) => rule.entityType === chainType && rule.active).sort((a, b) => a.stepOrder - b.stepOrder)
  const orgName = (id: string | null) => (id ? (organizations.data?.find((org) => org.id === id)?.name ?? id) : null)
  const forbidden = isForbidden(thresholds.error)
  const columns = editing.mayEdit ? 5 : 4

  return (
    <AdminPage
      titleKey="nav.adminThresholds"
      count={thresholds.data ? t('adm.thresholds.count', { count: all.length }) : undefined}
      leadKey="adm.thresholds.lead"
      readOnly={!editing.mayEdit}
      roKey="adm.thresholds.ro"
      forbidden={forbidden}
      primary={editing.mayEdit && (
        <PrimaryButton label={t('admin.threshold.add')} onClick={() => setDraft(emptyDraft(type !== 'all' ? type : (types[0] ?? 'invoice')))} />
      )}
    >
      <Notice text={editing.notice} />
      {!forbidden && all.length > 0 && (
        <div className="toolbar">
          <div className="seg" role="group" aria-label={t('admin.threshold.documentType')}>
            <button type="button" aria-pressed={type === 'all'} onClick={() => setType('all')}>{t('adm.thresholds.allTypes')}</button>
            {typesShown.map((candidate) => (
              <button key={candidate} type="button" aria-pressed={type === candidate} onClick={() => setType(candidate)}>
                {t(`admin.documentType.${candidate}`)}
                <span className="count-pill">{all.filter((rule) => rule.entityType === candidate).length}</span>
              </button>
            ))}
          </div>
          <div className="vsep" />
          <button type="button" className="chip" aria-pressed={activeOnly} onClick={() => setActiveOnly(!activeOnly)}>
            {t('adm.thresholds.activeOnly')} <span className="count">{all.filter((rule) => rule.active).length}</span>
          </button>
        </div>
      )}
      <AdminState
        isLoading={thresholds.isLoading}
        error={thresholds.error}
        onRetry={() => void thresholds.refetch()}
        forbiddenTitleKey="adm.thresholds.forbidden"
        errorTitleKey="adm.thresholds.error"
        skeleton={<SkeletonTable columns={columns} rows={10} />}
      >
        {all.length === 0 ? (
          <StateView boxed kind="empty" title={t('adm.thresholds.empty')} description={t('adm.thresholds.empty.text')}
                     actions={editing.mayEdit && (
                       <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDraft(emptyDraft(types[0] ?? 'invoice'))}>
                         <Icon name="plus" size="sm" />{t('admin.threshold.add')}
                       </button>
                     )} />
        ) : (
          <>
            {chainType && chain.length > 0 && (
              <section className="panel" aria-labelledby="ch-t">
                <div className="panel-h">
                  <span className="h2" id="ch-t">{t('adm.thresholds.chain', { type: t(`admin.documentType.${chainType}`) })}</span>
                  <span className="count">{t('adm.thresholds.steps', { count: chain.length })}</span>
                </div>
                <div className="panel-b stack" style={{ gap: 10 }}>
                  <div className="chain-h">
                    {chain.map((rule, index) => (
                      <Fragment key={rule.id}>
                        {index > 0 && <div className="chain-arrow"><Icon name="arrow-right" /></div>}
                        <div className="chain-card">
                          <span className="adm-step"><span className="ord">{rule.stepOrder}</span><span className="overline">{t('adm.thresholds.step', { n: rule.stepOrder })}</span></span>
                          <span className="strong">{conditionText(rule, t)}</span>
                          <span className="t-sm secondary row" style={{ gap: 6 }}>
                            <Icon name={rule.approverUserId ? 'user' : 'adm-users'} size="sm" />
                            {approverText(rule, approvers.data, t)}
                          </span>
                        </div>
                      </Fragment>
                    ))}
                  </div>
                  <p className="t-sm secondary">{t('adm.thresholds.chain.text')}</p>
                </div>
              </section>
            )}
            {visible.length === 0 ? (
              <StateView boxed kind="empty" title={t('adm.thresholds.noneHere')}
                         actions={<button type="button" className="btn btn-secondary btn-sm" onClick={() => {
                           setType('all')
                           setActiveOnly(false)
                         }}>{t('adm.resetFilters')}</button>} />
            ) : (
              <div className="tbl-wrap">
                <table className="tbl" aria-label={t('nav.adminThresholds')}>
                  <thead>
                    <tr>
                      <th style={{ width: 64 }}>{t('admin.threshold.step')}</th>
                      <th>{t('admin.threshold.condition')}</th>
                      <th>{t('admin.threshold.approver')}</th>
                      <th style={{ width: 130 }}>{t('adm.enabledColumn')}</th>
                      {editing.mayEdit && <th className="adm-act"><span className="sr-only">{t('adm.actions')}</span></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {typesShown.filter((candidate) => visible.some((rule) => rule.entityType === candidate)).map((candidate) => (
                      <Fragment key={candidate}>
                        <tr className="group">
                          <td colSpan={columns}>
                            <span className="row" style={{ gap: 8 }}>
                              <Icon name="file" />
                              {t(`admin.documentType.${candidate}`)}
                            </span>
                          </td>
                        </tr>
                        {visible.filter((rule) => rule.entityType === candidate).sort((a, b) => a.stepOrder - b.stepOrder).map((rule) => (
                          <tr key={rule.id} className={draft?.id === rule.id ? 'is-selected' : rule.active ? undefined : 'is-muted'}>
                            <td><span className="ord">{rule.stepOrder}</span></td>
                            <td className="wrap">
                              <span className="stack" style={{ gap: 2 }}>
                                <Tokens join={t('admin.threshold.or')} items={conditionTokens(rule, t, locale)} />
                                <span className="t-sm muted">
                                  {rule.name}
                                  {orgName(rule.organizationId) && ` · ${orgName(rule.organizationId)}`}
                                  {rule.escalateAfterHours != null && ` · ${t('adm.thresholds.escalates', { hours: rule.escalateAfterHours })}`}
                                </span>
                              </span>
                            </td>
                            <td><Approver rule={rule} approvers={approvers.data} /></td>
                            <td>
                              {editing.mayEdit ? (
                                <Switch checked={rule.active} disabled={toggle.isPending}
                                        ariaLabel={t('adm.thresholds.rule', { name: rule.name })} onChange={() => toggle.mutate(rule)} />
                              ) : (
                                <OnOffBadge on={rule.active} />
                              )}
                            </td>
                            {editing.mayEdit && (
                              <td className="adm-act">
                                <RowMenu label={t('adm.thresholds.actions', { name: rule.name })} items={[
                                  { id: 'edit', label: t('admin.edit'), icon: 'adm-edit', onSelect: () => setDraft(fromThreshold(rule)) },
                                ]} />
                              </td>
                            )}
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="t-sm muted">{t('admin.threshold.hint')}</p>
          </>
        )}
      </AdminState>
      {draft && (
        <ThresholdModal draft={draft} documentTypes={types} approvers={approvers.data} organizations={organizations.data ?? []}
                        onChange={setDraft} onClose={() => setDraft(null)} onSaved={() => {
                          setDraft(null)
                          editing.clear()
                          void queryClient.invalidateQueries({ queryKey: ['admin-thresholds'] })
                        }} />
      )}
    </AdminPage>
  )
}

function Approver({ rule, approvers }: { rule: Threshold; approvers: Approvers | undefined }) {
  const { t } = useApp()
  if (rule.approverUserId) {
    const user = approvers?.users.find((candidate) => candidate.id === rule.approverUserId)
    return (
      <span className="row" style={{ gap: 6 }}>
        <span className="ava">{initials(user?.fullName)}</span>
        {user?.fullName ?? rule.approverUserId}
      </span>
    )
  }
  if (rule.approverRoleId) {
    const role = approvers?.roles.find((candidate) => candidate.id === rule.approverRoleId)
    return (
      <span className="row" style={{ gap: 6 }}>
        <span className="badge b-outline"><Icon name="adm-users" />{t('adm.thresholds.role')}</span>
        {role?.name ?? rule.approverRoleId}
      </span>
    )
  }
  return <span className="muted">—</span>
}

function ThresholdModal({
  draft,
  documentTypes,
  approvers,
  organizations,
  onChange,
  onClose,
  onSaved,
}: {
  draft: Draft
  documentTypes: string[]
  approvers: Approvers | undefined
  organizations: { id: string; name: string }[]
  onChange: (draft: Draft) => void
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useApp()
  const formId = useId()
  const [error, setError] = useState<string | null>(null)
  const currencies = useQuery({ queryKey: ['admin-currencies'], queryFn: ({ signal }) => fetchCurrencies(signal) })
  const save = useMutation({
    mutationFn: () => (draft.id ? updateThreshold(draft.id, toInput(draft)) : createThreshold(toInput(draft))),
    onSuccess: onSaved,
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })
  const byUser = draft.approverKind === 'user'
  // Neither discount nor amount is not "every document": the server needs at least one, and
  // the empty rule is refused here, under the fields, rather than after a round trip.
  const noCondition = draft.minDiscountPercent.trim() === '' && draft.minAmount.trim() === ''
  const noApprover = byUser ? draft.approverUserId === '' : draft.approverRoleId === ''

  return (
    <EditLayer
      kind="modal"
      overline={t('adm.thresholds.overline')}
      title={draft.id
        ? t('adm.thresholds.editTitle', { type: t(`admin.documentType.${draft.entityType}`), n: draft.stepOrder })
        : t('adm.thresholds.newTitle')}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={t('admin.save')} pending={save.isPending}
                            disabled={noCondition || noApprover} form={formId} />}
    >
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('admin.threshold.form')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        save.mutate()
      }}>
        <div className="form-grid">
          {!draft.id ? (
            <Field label={t('admin.threshold.documentType')} htmlFor="th-type" required>
              <select id="th-type" className="input" value={draft.entityType} onChange={(e) => onChange({ ...draft, entityType: e.target.value })}>
                {documentTypes.map((type) => (
                  <option key={type} value={type}>{t(`admin.documentType.${type}`)}</option>
                ))}
              </select>
            </Field>
          ) : (
            <Field label={t('admin.threshold.documentType')}>
              <div className="input is-readonly" aria-readonly="true">{t(`admin.documentType.${draft.entityType}`)}</div>
            </Field>
          )}
          <Field label={t('admin.threshold.step')} htmlFor="th-step" help={t('adm.thresholds.step.help')}>
            <input id="th-step" type="number" min={1} className="input" value={draft.stepOrder}
                   onChange={(e) => onChange({ ...draft, stepOrder: e.target.value })} />
          </Field>
          <Field label={t('admin.threshold.name')} htmlFor="th-name" required span2>
            <input id="th-name" className="input" value={draft.name} onChange={(e) => onChange({ ...draft, name: e.target.value })} />
          </Field>
        </div>
        <Field label={t('admin.threshold.condition')} error={noCondition ? t('adm.thresholds.needCondition') : null}
               help={t('adm.thresholds.condition.help')}>
          <div className="stack" style={{ gap: 6 }}>
            <div className="adm-cond">
              <span className="fb-join">{t('adm.thresholds.if')}</span>
              <label className="label" htmlFor="th-discount">{t('admin.threshold.minDiscount')}</label>
              <span className="t-sm muted">{t('adm.thresholds.from')}</span>
              <div className="input input-sm">
                <input id="th-discount" className="adm-bare is-num" inputMode="decimal" value={draft.minDiscountPercent}
                       onChange={(e) => onChange({ ...draft, minDiscountPercent: e.target.value })} />
                <span className="suffix">%</span>
              </div>
              <span />
            </div>
            <div className="adm-cond">
              <span className="fb-join">{t('admin.threshold.or')}</span>
              <label className="label" htmlFor="th-amount">{t('admin.threshold.minAmount')}</label>
              <span className="t-sm muted">{t('adm.thresholds.from')}</span>
              <div className="input input-sm">
                <input id="th-amount" className="adm-bare is-num" inputMode="decimal" value={draft.minAmount}
                       onChange={(e) => onChange({ ...draft, minAmount: e.target.value })} />
                <select className="adm-bare" style={{ flex: 'none', width: 'auto' }} aria-label={t('admin.threshold.currency')}
                        value={draft.currency} onChange={(e) => onChange({ ...draft, currency: e.target.value })}>
                  <option value="">—</option>
                  {(currencies.data ?? []).map((currency) => (
                    <option key={currency.code} value={currency.code}>{currency.code}</option>
                  ))}
                </select>
              </div>
              <span />
            </div>
          </div>
        </Field>
        <Field label={t('admin.threshold.approver')} required error={noApprover ? t('adm.thresholds.needApprover') : null}
               help={t('adm.thresholds.approver.help')}>
          <div className="row" style={{ gap: 8 }}>
            <div className="seg" role="group" aria-label={t('adm.thresholds.approverKind')}>
              <button type="button" aria-pressed={!byUser}
                      onClick={() => onChange({ ...draft, approverKind: 'role', approverUserId: '' })}>
                <Icon name="adm-users" size="sm" />{t('adm.thresholds.role')}
              </button>
              <button type="button" aria-pressed={byUser}
                      onClick={() => onChange({ ...draft, approverKind: 'user', approverRoleId: '' })}>
                <Icon name="user" size="sm" />{t('adm.thresholds.user')}
              </button>
            </div>
            {byUser ? (
              <select className="input" style={{ flex: 1 }} aria-label={t('admin.threshold.approverUser')} value={draft.approverUserId}
                      onChange={(e) => onChange({ ...draft, approverUserId: e.target.value })}>
                <option value="">—</option>
                {(approvers?.users ?? []).map((user) => (
                  <option key={user.id} value={user.id}>{user.fullName}</option>
                ))}
              </select>
            ) : (
              <select className="input" style={{ flex: 1 }} aria-label={t('admin.threshold.approverRole')} value={draft.approverRoleId}
                      onChange={(e) => onChange({ ...draft, approverRoleId: e.target.value })}>
                <option value="">—</option>
                {(approvers?.roles ?? []).map((role) => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
            )}
          </div>
        </Field>
        <div className="form-grid">
          <Field label={t('admin.threshold.organization')} htmlFor="th-org">
            <select id="th-org" className="input" value={draft.organizationId} onChange={(e) => onChange({ ...draft, organizationId: e.target.value })}>
              <option value="">{t('admin.threshold.allOrganizations')}</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </Field>
          <Field label={t('admin.threshold.escalate')} htmlFor="th-esc" help={t('adm.thresholds.escalate.help')}>
            <input id="th-esc" type="number" min={1} className="input" value={draft.escalateAfterHours}
                   onChange={(e) => onChange({ ...draft, escalateAfterHours: e.target.value })} />
          </Field>
        </div>
        <Switch checked={draft.active} label={t('adm.thresholds.ruleOn')} onChange={(active) => onChange({ ...draft, active })} />
        <div className="alert alert-neutral">
          <Icon name="adm-info" />
          <span>{t('admin.threshold.hint')}</span>
        </div>
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}

/** Form state: strings, as inputs hold them; converted once, on save. */
interface Draft {
  id: string | null
  entityType: string
  name: string
  organizationId: string
  minDiscountPercent: string
  minAmount: string
  currency: string
  approverKind: 'role' | 'user'
  approverRoleId: string
  approverUserId: string
  stepOrder: string
  escalateAfterHours: string
  active: boolean
}

function emptyDraft(entityType: string): Draft {
  return {
    id: null,
    entityType,
    name: '',
    organizationId: '',
    minDiscountPercent: '',
    minAmount: '',
    currency: '',
    approverKind: 'role',
    approverRoleId: '',
    approverUserId: '',
    stepOrder: '1',
    escalateAfterHours: '',
    active: true,
  }
}

function fromThreshold(threshold: Threshold): Draft {
  return {
    id: threshold.id,
    entityType: threshold.entityType,
    name: threshold.name,
    organizationId: threshold.organizationId ?? '',
    minDiscountPercent: threshold.minDiscountPercent?.toString() ?? '',
    minAmount: threshold.minAmount?.toString() ?? '',
    currency: threshold.currency ?? '',
    approverKind: threshold.approverUserId ? 'user' : 'role',
    approverRoleId: threshold.approverRoleId ?? '',
    approverUserId: threshold.approverUserId ?? '',
    stepOrder: String(threshold.stepOrder),
    escalateAfterHours: threshold.escalateAfterHours?.toString() ?? '',
    active: threshold.active,
  }
}

/**
 * Empty stays null, not zero: a blank discount means "no discount rule", while 0 would be
 * a rule that fires on every document. The comma is accepted, as Russian users type it.
 */
function decimal(value: string): number | null {
  const trimmed = value.trim().replace(/\s/g, '').replace(',', '.')
  return trimmed === '' ? null : Number(trimmed)
}

function toInput(draft: Draft): ThresholdInput {
  return {
    entityType: draft.entityType,
    name: draft.name,
    organizationId: draft.organizationId || null,
    minDiscountPercent: decimal(draft.minDiscountPercent),
    minAmount: decimal(draft.minAmount),
    currency: draft.currency || null,
    approverRoleId: draft.approverKind === 'role' ? draft.approverRoleId || null : null,
    approverUserId: draft.approverKind === 'user' ? draft.approverUserId || null : null,
    stepOrder: Number(draft.stepOrder || '1'),
    escalateAfterHours: decimal(draft.escalateAfterHours),
    active: draft.active,
  }
}

type T = (key: string, p?: Record<string, string | number>) => string

function conditionTokens(threshold: Threshold, t: T, locale: string) {
  const items: { k: string; op: string; v: string }[] = []
  if (threshold.minDiscountPercent != null) {
    items.push({ k: t('adm.thresholds.discount'), op: t('adm.thresholds.from'), v: `${threshold.minDiscountPercent} %` })
  }
  if (threshold.minAmount != null) {
    items.push({ k: t('adm.thresholds.amount'), op: t('adm.thresholds.from'), v: formatMoney(threshold.minAmount, threshold.currency ?? null, locale) })
  }
  return items
}

function conditionText(threshold: Threshold, t: T) {
  const parts: string[] = []
  if (threshold.minDiscountPercent != null) {
    parts.push(t('admin.threshold.discountFrom', { value: threshold.minDiscountPercent }))
  }
  if (threshold.minAmount != null) {
    parts.push(t('admin.threshold.amountFrom', { value: threshold.minAmount, currency: threshold.currency ?? '' }))
  }
  return parts.join(` ${t('admin.threshold.or')} `)
}

function approverText(rule: Threshold, approvers: Approvers | undefined, t: T) {
  if (rule.approverUserId) return approvers?.users.find((user) => user.id === rule.approverUserId)?.fullName ?? '—'
  const role = approvers?.roles.find((candidate) => candidate.id === rule.approverRoleId)?.name
  return role ? t('adm.thresholds.byRole', { role }) : '—'
}
